import React, { useEffect, useMemo, useState } from "react";
import { formatDateOnly } from "helpers/dateOnly";
import { Box, Flex, Grid, Spinner } from "theme-ui";
import { useNavigate, useParams } from "react-router-dom";

import Header from "components/ui/Header";
import Page from "components/ui/Page";
import H5 from "components/typography/H5";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import BasicButton from "components/forms/BasicButton";
import { mnt, mntCard, mntType, mntStepShade, mntPill } from "theme/maintenanceTokens";

import { getWorkOrder, priceClientWorkOrder, requestWorkOrderCancellation, withdrawWorkOrderCancellation } from "api/ServicePlans";
import { getQuoteRequest, generateQuoteFromEstimate } from "api/QuoteRequests";
import { getQuote, acceptOrDeny } from "api/Quotes";
import { declineQuote, lockPrice, getExpirationSettings } from "api/QuoteExpiration";
import DeclineReasonModal from "components/DeclineReasonModal";
import PriceLockModal from "components/PriceLockModal";

/**
 * CLIENT view of a work order (Session 62) — a lean, read-only JOB STATUS
 * page, rendered on the SAME route as the operator case file
 * (`/maintenance/work-orders/:id`) and gated by role in WorkOrderDetail.
 *
 * This mirrors how Projects treats clients: the same URL serves a limited
 * view with limited actions, not the staff console with fields hidden.
 * The operator `WorkOrderDetail` (the 4000-line pricing/provider/PO/
 * Corrigo console) is never mounted for a client — this component is.
 *
 * The backend `GET /maintenance/work-orders/:id` already (a) allows the
 * owning client, parent-aware, and (b) FIELD-SCOPES the payload for a
 * client: NTE, provider, cost, margin, PO, batch and raw intake are
 * stripped server-side before the response leaves. So this component
 * only ever receives client-safe data; it does not itself have to be the
 * security boundary. It reads the WO's own records (its quote, project,
 * invoice) — which carry the price the client is quoted / billed — and
 * shows a status ladder, the site, and the two actions a client already
 * has elsewhere: accept their quote, and pay their invoice, both by
 * linking into the existing standalone Quote / Invoice pages (which are
 * already client-capable and deposit-aware). Nothing new is invented.
 */

// The client ladder — a positive, monotonic relabel of the operator's
// per-record bars. Internal legs (RFQ intake mechanics, the provider PO)
// are folded away; the words are what a customer expects to read.
const CLIENT_LADDER = [
  "Requested",
  "Quoted",
  "Approved",
  "Scheduled",
  "In progress",
  "Complete",
  "Invoiced",
] as const;

type Rung = (typeof CLIENT_LADDER)[number];

/**
 * The maintenance SECONDARY button, spelled out.
 *
 * `BasicButton scheme="secondary"` resolves to the GLOBAL theme's
 * secondary variant, which is the olive/green fill the trunk uses. On a
 * maintenance surface that is wrong twice over: the token convention
 * (theme/maintenanceTokens.ts) says secondary is a cardBg fill with a
 * `divider` border and ink text, and it says status hues — green
 * included — are for PILLS AND TEXT ONLY. A green filled button next to
 * the ink primary reads as a second, competing primary, which is exactly
 * backwards for a quiet action like asking us to cancel.
 */
const mntSecondaryBtn = {
  background: mnt.cardBg,
  border: "1px solid " + mnt.divider,
  color: mnt.ink,
};

/**
 * Cancellation reason options (Session 80). The VALUES must match the
 * server's whitelist in `WorkOrderService.CANCELLATION_REASON_CATEGORIES`
 * exactly — an unknown category 400s rather than being coerced, so that
 * a rollup never counts a category nobody chose. The blank first option
 * is real: the reason is COLLECTED, NOT REQUIRED, and a client who
 * cannot articulate why still gets to ask.
 */
const CANCELLATION_REASONS: { value: string; label: string }[] = [
  { value: "", label: "Prefer not to say" },
  { value: "no_longer_needed", label: "We no longer need this work" },
  { value: "budget", label: "Budget" },
  { value: "scheduling", label: "Timing or scheduling" },
  { value: "duplicate_request", label: "Duplicate request" },
  { value: "wrong_site", label: "Wrong site" },
  { value: "other", label: "Something else" },
];

/**
 * Map the operator's stamped record bars (`wo.stageRecords`, each
 * { key, state, ordinal }) + `wo.caseClosed` to the client ladder rung
 * the job sits on. Reads the SAME derived bars the operator strip reads,
 * so the client status and the operator status can never disagree about
 * where the work is — only the vocabulary differs. Terminal internal
 * states (superseded/expired quote, cancelled project) never pull the
 * ladder backward: the client sees the furthest positive rung the live
 * records support.
 */
function clientRung(wo: any): { rung: Rung; step: number } {
  const bars: any[] = Array.isArray(wo?.stageRecords) ? wo.stageRecords : [];
  const bar = (k: string) => bars.find((b) => b?.key === k) ?? null;
  const invoice = bar("invoice");
  const project = bar("project");
  const quote = bar("quote");
  const at = (r: Rung) => ({ rung: r, step: CLIENT_LADDER.indexOf(r) });

  if (wo?.caseClosed) return at("Invoiced");
  if (invoice && invoice.state !== "not_started" && invoice.state !== "terminal") {
    return at("Invoiced");
  }
  if (project && project.state !== "not_started" && project.state !== "terminal") {
    const step = Number(project.ordinal) || 0; // ProjectStatus.stepNumber
    if (project.state === "complete" || step >= 5) return at("Complete");
    if (step >= 4) return at("In progress"); // Scheduled+ but not Complete
    return at("Scheduled"); // New / Verified / Site Ready
  }
  if (quote && quote.state !== "not_started") {
    if (quote.state === "complete") return at("Approved"); // Accepted
    if (quote.state !== "terminal") return at("Quoted"); // live, not accepted
  }
  return at("Requested");
}

/**
 * The named sub-steps INSIDE each client phase — the "what's happening
 * right now" ladder that expands under the current rung, mirroring the
 * operator's RecordStepPanel but in client vocabulary. Deliberately
 * short and plain (Jack's steer: keep it simple, no invented signal).
 *
 * For the CURRENT phase we resolve which sub-step is live from data the
 * client already receives — the quote status, the project's stepNumber,
 * the invoice status — so the ladder tracks real state, not a guess.
 * Phases before the current one are all done; phases after are all
 * future. Returns the sub-steps with per-step state (done/current/
 * future) plus how many are done, so the bar fills by real depth.
 */
const PHASE_SUBSTEPS: Record<Rung, string[]> = {
  Requested: ["Received", "Being reviewed"],
  Quoted: ["Quote prepared", "Sent to you", "Awaiting approval"],
  Approved: ["Quote accepted", "Scheduling"],
  Scheduled: ["Date set", "Ready for the crew"],
  "In progress": ["Work underway", "Wrapping up"],
  Complete: ["Work finished", "Being invoiced"],
  Invoiced: ["Invoice sent", "Payment due", "Paid"],
};

/**
 * While a deposit is in play the third "Quoted" sub-step is wrong: the
 * client has acted, so "Awaiting approval" reads as though we are still
 * waiting on them to decide. What we are actually waiting on is the
 * deposit, and then their confirmation once it lands.
 *
 * NOTE this is interim. The honest state — "you accepted, we are waiting
 * on the deposit" — does not exist yet: acceptance is not recorded until
 * the deposit is paid, because `acceptOrDeny` creates the provider PO.
 * See SESSION_STATE Session 78, "OPEN — accept without paying the
 * deposit". Until that gate is built these labels say what is true.
 */
const QUOTED_DEPOSIT_DUE = ["Quote prepared", "Sent to you", "Waiting on your deposit"];
const QUOTED_DEPOSIT_IN = ["Quote prepared", "Sent to you", "Deposit received"];

/**
 * Resolve the live sub-step INDEX within the current phase from the
 * client-visible records. Returns the 0-based index of the sub-step the
 * job currently sits on inside `rung`; earlier sub-steps read done, this
 * one current, later ones future. Kept conservative — when there is no
 * finer signal, it sits on the FIRST sub-step (the phase has been
 * entered) rather than inventing progress.
 */
function currentSubStepIndex(rung: Rung, wo: any, quote: any, invoice: any, project: any): number {
  switch (rung) {
    case "Quoted": {
      // 0 prepared, 1 sent to you, 2 awaiting approval. A live quote the
      // client can act on has been sent and is awaiting them.
      const s = Number(quote?.status);
      if (s === 0 || s === 3 || s === 6) return 2; // ready to review -> awaiting approval
      return 1; // generated, on its way
    }
    case "Invoiced": {
      // 0 sent, 1 payment due, 2 paid.
      if (invoice?.status === 2) return 2; // Paid
      if (wo?.caseClosed) return 2;
      if (invoice?.status === 3) return 1; // partly paid -> payment due
      return 1; // sent, payment due
    }
    case "In progress": {
      // Wrapping up once the project is at/after Scheduled with an end in
      // sight; otherwise underway. Only a coarse split is honest here.
      const step = Number(project?.status?.stepNumber ?? project?.ordinal ?? 0);
      return step >= 4 ? 1 : 0;
    }
    // Requested / Approved / Scheduled / Complete have no finer
    // client-visible signal — sit on the first sub-step (phase entered).
    default:
      return 0;
  }
}

// Quote.status: 0 New · 1 Accepted · 2 Denied · 3 Draft · 4 Expired ·
// 5 Superseded · 6 Price Locked. The client only ever sees their newest
// non-superseded quote.
function quoteStatusWord(status: number): string {
  switch (status) {
    case 1:
      return "Accepted";
    case 2:
      return "Declined";
    case 4:
      return "Expired";
    case 6:
      return "Price locked";
    default:
      return "Ready to review"; // 0 New / 3 Draft
  }
}

function pad(n: any): string {
  return String(n ?? 0).padStart(5, "0");
}

function fmtMoney(n: any): string {
  return Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
}

/**
 * Total price for one priced estimate service — the SAME math the trunk
 * RFQ surface uses (Items.tsx calculateTotalPrice): the service line plus
 * any addon with a positive quantity. Price only. `cost`, `markup` and
 * `depot.provider` also ride on this payload and are never rendered here.
 */
function serviceTotal(s: any): number {
  const base = Number(s?.quantity || 0) * Number(s?.price || 0);
  const addons = Array.isArray(s?.addons)
    ? s.addons.reduce(
        (sum: number, a: any) =>
          sum + (Number(a?.quantity) > 0 ? Number(a.quantity) * Number(a?.price || 0) : 0),
        0,
      )
    : 0;
  return base + addons;
}

function fmtDate(d: any): string {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function WorkOrderClientStatus() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [wo, setWo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // Bumped after any write on this page (pricing, quote generation,
  // approval, decline) to re-read the work order in place. The client
  // never navigates away, so every action has to refresh the records the
  // ladder and the panels below are derived from.
  const [reloadKey, setReloadKey] = useState(0);

  /**
   * CLIENT PRICING PANEL state (Session 78, WO_PRICING_IN_DETAIL §3).
   * The RFQ read supplies the priced service options; the quote read
   * supplies what the approval branch needs (deposit requirement, total).
   */
  const [rfq, setRfq] = useState<any>(null);
  const [rfqLoading, setRfqLoading] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [fullQuote, setFullQuote] = useState<any>(null);
  const [acting, setActing] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  // Whether the quote read has COME BACK, win or lose. Distinct from
  // `fullQuote` being null: until we know the billed client's deposit
  // requirement we must not offer approval in place, and a read that
  // failed is not the same as one still in flight.
  const [quoteLoaded, setQuoteLoaded] = useState(false);
  // The deposit rail. NOT reimplemented here — `PriceLockModal` is the
  // same component the trunk quote page mounts, driven by the same
  // `lockPrice` / `recordDeposit` / Stripe / Plaid calls, all of which
  // are isClient-permitted. The work order just opens it.
  const [showLockModal, setShowLockModal] = useState(false);
  const [lockStep, setLockStep] = useState<"choose" | "pay">("choose");
  const [expirationSettings, setExpirationSettings] = useState<any>(null);

  /**
   * Client-initiated pricing for a work order that has none yet.
   * See the "Get it priced" block in the active-stage card.
   */
  const [priceQty, setPriceQty] = useState<string>("");
  const [pricing, setPricing] = useState(false);
  const [priceError, setPriceError] = useState<string | null>(null);

  // CANCELLATION REQUEST (Session 80). The client ASKS; they never
  // cancel. `cancelFormOpen` keeps the ask behind one deliberate click —
  // it must never sit next to Approve as a same-weight choice.
  const [cancelFormOpen, setCancelFormOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState<string>("");
  const [cancelCategory, setCancelCategory] = useState<string>("");
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const priceThisWorkOrder = async () => {
    if (!(Number(priceQty) >= 1) || pricing) return;
    setPricing(true);
    setPriceError(null);
    const res: any = await priceClientWorkOrder({
      data: {
        planId: wo?.servicePlan?.id,
        workOrderId: wo?.id,
        quantity: Number(priceQty),
      },
    });
    setPricing(false);
    if (res?.success && (res.rfqId || res.quoteId)) {
      // Session 78: the client STAYS on the work order. Re-reading the WO
      // brings back the priced RFQ (or the quote), which the pricing
      // panel below renders in place. The old redirect to
      // /quote-requests/:id was the last place a client was pushed off
      // their own work order.
      setPriceQty("");
      setReloadKey((k) => k + 1);
      return;
    }
    // NEVER show the raw server message to a client. The real ones are
    // "No availability for selected product and location" and
    // "Forbidden." — the first is a depot-coverage fact stated in our
    // vocabulary, the second is meaningless to anyone. Both get logged
    // for us and replaced with something true and useful for them.
    console.error("[WorkOrderClientStatus] pricing failed, raw response:", res);
    setPriceError(
      "We could not price this one automatically — this site needs a hand-built quote. Our team has been notified and will send you one.",
    );
  };

  /**
   * Ask us to cancel. This does NOT cancel anything — cancelling
   * auto-cancels a dispatched provider's purchase order and refuses to
   * touch a paid invoice, so it is a human call by construction. The
   * copy below never implies the work has stopped.
   */
  const submitCancellationRequest = async () => {
    if (cancelSubmitting) return;
    setCancelSubmitting(true);
    setCancelError(null);
    const res: any = await requestWorkOrderCancellation({
      data: {
        id,
        reason: cancelReason.trim() || null,
        reasonCategory: cancelCategory || null,
      },
    });
    setCancelSubmitting(false);
    if (res?.id) {
      setCancelFormOpen(false);
      setCancelReason("");
      setCancelCategory("");
      setReloadKey((k) => k + 1);
      return;
    }
    // Never surface the raw server message to a client (Session 78
    // rule). The reachable failures here are network or a stale tab on a
    // work order that just closed; neither is worth a server sentence.
    console.error("[WorkOrderClientStatus] cancellation request failed:", res);
    setCancelError(
      "We could not send that request just now. Please try again, or contact us and we will sort it out.",
    );
  };

  const withdrawCancellationRequest = async () => {
    if (cancelSubmitting) return;
    setCancelSubmitting(true);
    setCancelError(null);
    const res: any = await withdrawWorkOrderCancellation({ data: { id } });
    setCancelSubmitting(false);
    if (res?.id) {
      setReloadKey((k) => k + 1);
      return;
    }
    console.error("[WorkOrderClientStatus] withdraw failed:", res);
    setCancelError(
      "We could not withdraw that just now. Please try again, or contact us and we will sort it out.",
    );
  };

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Project status";
  }, []);

  useEffect(() => {
    let alive = true;
    // Only the FIRST read shows the page spinner. A reload after an
    // action refreshes underneath the panel the client is looking at.
    if (reloadKey === 0) setLoading(true);
    getWorkOrder({ data: { id } })
      .then((res: any) => {
        if (!alive) return;
        if (res?.id) {
          setWo(res);
          setNotFound(false);
        } else {
          setNotFound(true);
        }
        setLoading(false);
      })
      .catch(() => {
        if (alive) {
          setNotFound(true);
          setLoading(false);
        }
      });
    return () => {
      alive = false;
    };
  }, [id, reloadKey]);

  // Newest non-superseded quote — the client's live quote.
  const quote = useMemo(() => {
    const quotes: any[] = Array.isArray(wo?.convertedRfq?.quotes)
      ? wo.convertedRfq.quotes
      : [];
    if (!quotes.length) return null;
    const newest = [...quotes].sort(
      (a, b) => (b?.sequence ?? 0) - (a?.sequence ?? 0),
    )[0];
    if (!newest?.id || newest.status === 5) return null; // superseded
    return newest;
  }, [wo]);

  const invoice = useMemo(() => {
    const inv = quote?.invoice ?? null;
    if (!inv?.id || inv.status === 4) return null; // voided
    return inv;
  }, [quote]);

  const project = useMemo(
    () => wo?.convertedRfq?.project ?? quote?.project ?? null,
    [wo, quote],
  );

  const ladder = useMemo(() => (wo ? clientRung(wo) : null), [wo]);

  /* ─────────────────────────────────────────────────────────────────
   * CLIENT PRICING PANEL — WO_PRICING_IN_DETAIL.md §3, client-simplified.
   *
   * §3 makes the WO detail view the pricing surface. This is the client
   * cut of that stage model:
   *   RFQ generated  -> the priced service options, selectable, with
   *                     Generate quote as the primary action
   *   quote generated -> approve / decline in place
   * Provider swap, price override and the NTE delta are DROPPED — all
   * three are operator concerns (and cost/provider never render here
   * even though the shared RFQ payload carries them).
   *
   * No new pricing logic and no new endpoint: getQuoteRequest,
   * generateQuoteFromEstimate, acceptOrDeny and declineQuote are the
   * same calls the trunk RFQ and Quote surfaces already make, all of
   * which allow isClient.
   *
   * The in-place panel is part of the per-plan self-service grant
   * (CLIENT_SELF_SERVICE_BUILD §4.1), so it is gated on
   * `clientSelfServiceEnabled`. Without the grant the page behaves
   * exactly as it did before — the record cards link out.
   * ───────────────────────────────────────────────────────────────── */
  const selfServiceOn = !!wo?.servicePlan?.clientSelfServiceEnabled;

  // The RFQ's priced options, read only while there is no live quote yet
  // (once a quote exists the options are settled and the panel switches).
  useEffect(() => {
    let alive = true;
    const rfqId = wo?.convertedRfq?.id;
    if (!rfqId || quote?.id || !selfServiceOn) {
      setRfq(null);
      return;
    }
    setRfqLoading(true);
    getQuoteRequest({ data: { id: rfqId } })
      .then((res: any) => {
        if (!alive) return;
        const ok = res?.id ? res : null;
        setRfq(ok);
        const services: any[] = Array.isArray(ok?.estimate?.services)
          ? ok.estimate.services
          : [];
        const preselected = services.find((s: any) => s?.selected) ?? services[0];
        setSelectedServiceId(preselected?.id ?? null);
        setRfqLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setRfq(null);
        setRfqLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [wo?.convertedRfq?.id, quote?.id, selfServiceOn, reloadKey]);

  // The full quote. The WO payload carries enough for the status ladder
  // but not the two things the approval branch turns on — the billed
  // client's deposit requirement and the quote total.
  useEffect(() => {
    let alive = true;
    if (!quote?.id || !selfServiceOn) {
      setFullQuote(null);
      setQuoteLoaded(false);
      return;
    }
    setQuoteLoaded(false);
    getQuote({ data: { id: quote.id } })
      .then((res: any) => {
        if (!alive) return;
        setFullQuote(res?.id ? res : null);
        setQuoteLoaded(true);
      })
      .catch(() => {
        if (!alive) return;
        setFullQuote(null);
        setQuoteLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, [quote?.id, selfServiceOn, reloadKey]);

  // Price-lock / deposit settings — the same read the trunk quote page
  // makes before opening the deposit modal (it supplies the default
  // deposit percent and max lock days). Client-permitted; the modal
  // treats it as optional, so a failed read is not fatal.
  useEffect(() => {
    let alive = true;
    if (!quote?.id || !selfServiceOn) return;
    getExpirationSettings()
      .then((res: any) => {
        if (alive) setExpirationSettings(res ?? null);
      })
      .catch(() => {
        /* optional */
      });
    return () => {
      alive = false;
    };
  }, [quote?.id, selfServiceOn]);

  const pricedOptions: any[] = useMemo(() => {
    const services: any[] = Array.isArray(rfq?.estimate?.services)
      ? rfq.estimate.services
      : [];
    return [...services].sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0));
  }, [rfq]);

  const onGenerateQuote = async () => {
    const estimateId = rfq?.estimate?.id;
    if (!estimateId || !selectedServiceId || generating) return;
    setGenerating(true);
    setPanelError(null);
    // The trunk call takes the ESTIMATE id, not the RFQ id (Items.tsx).
    const res: any = await generateQuoteFromEstimate({
      data: { id: estimateId },
      service: { selectServices: [selectedServiceId] },
    });
    setGenerating(false);
    if (res?.id) {
      setRfq(null);
      setReloadKey((k) => k + 1);
      return;
    }
    console.error("[WorkOrderClientStatus] generate quote failed, raw response:", res);
    setPanelError(
      "We could not put that quote together just now. Try again in a moment, or reach out and we will send it over.",
    );
  };

  /**
   * Deposit-required acceptance — the SAME rail as the trunk quote page
   * (Quotes/view.tsx handleRequireDeposit). Lock the price at the
   * client's configured deposit percentage, then open the pay step. The
   * PROJECT is not created here: it is created by the confirm action
   * below once the deposit has landed, exactly as status 6 works on the
   * trunk. Same process, guided more simply.
   */
  const onPayDepositAndApprove = async () => {
    if (!quote?.id || acting) return;
    const pct = (fullQuote?.client?.depositPercentage ??
      fullQuote?.primaryClient?.depositPercentage ??
      50) as number;
    setActing(true);
    setPanelError(null);
    const res: any = await lockPrice(quote.id, { depositPercent: pct });
    if (res?.success) {
      // Re-read the quote BEFORE opening the modal, as the trunk does
      // (`await _init()` there): the pay step needs the lock fields the
      // lock just wrote (deposit amount, deadline), and the WO reload
      // below is async.
      const fresh: any = await getQuote({ data: { id: quote.id } });
      if (fresh?.id) setFullQuote(fresh);
      setActing(false);
      setLockStep("pay");
      setShowLockModal(true);
      setReloadKey((k) => k + 1);
      return;
    }
    setActing(false);
    console.error("[WorkOrderClientStatus] deposit setup failed, raw response:", res);
    setPanelError(
      "We could not set up the deposit just now. Try again in a moment, or reach out and we will send you a payment link.",
    );
  };

  const onAcceptQuote = async () => {
    if (!quote?.id || acting) return;
    setActing(true);
    setPanelError(null);
    const res: any = await acceptOrDeny({
      data: { id: quote.id },
      update: { status: 1, discountFlag: false },
    });
    setActing(false);
    if (res?.quote) {
      setReloadKey((k) => k + 1);
      return;
    }
    console.error("[WorkOrderClientStatus] accept failed, raw response:", res);
    setPanelError(
      "We could not record your approval just now. Try again in a moment, or reach out and we will confirm it for you.",
    );
  };

  const onDeclineQuote = async (data: {
    category: string | null;
    notes?: string;
    competitorName?: string;
  }) => {
    if (!quote?.id || acting) return;
    setActing(true);
    setPanelError(null);
    // The same decline path the trunk quote page uses, so the reason
    // category still lands in the decline analytics.
    const res: any = await declineQuote(quote.id, data);
    setActing(false);
    setShowDecline(false);
    if (res?.success) {
      setReloadKey((k) => k + 1);
      return;
    }
    console.error("[WorkOrderClientStatus] decline failed, raw response:", res);
    setPanelError(
      "We could not record that just now. Try again in a moment, or reach out and we will take care of it.",
    );
  };

  if (loading) {
    return (
      <>
        <Header></Header>
        <Page title="Project status">
          <Box sx={{ textAlign: "center", paddingY: "40px" }}>
            <Spinner />
          </Box>
        </Page>
      </>
    );
  }

  if (notFound || !wo) {
    return (
      <>
        <Header></Header>
        <Page title="Project status">
          <Box
            sx={{
              ...mntCard,
              padding: "24px",
              textAlign: "center",
              maxWidth: "600px",
              marginX: "auto",
            }}
          >
            <Subtext fontSize={"14px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
              This project could not be found on your account.
            </Subtext>
          </Box>
        </Page>
      </>
    );
  }

  const address = wo?.lcp?.location?.address ?? null;
  const site =
    wo?.lcp?.name || wo?.lcp?.location?.name || address?.line1 || "Site";
  const cityLine = address
    ? [address.city, address.state].filter(Boolean).join(", ")
    : "";
  const addressLine = address
    ? [address.line1, address.city, address.state, address.postalCode]
        .filter(Boolean)
        .join(", ")
    : "";
  const serviceType = wo?.service?.name ?? null;
  const number = wo?.externalId ?? null;
  const name = wo?.name ?? null;
  const title = number ? `Work order ${number}` : name || "Project status";
  const secondary = number && name ? name : null;
  const corrigoManaged = wo?.servicePlan?.intakeStyle === "work_order_fed";

  // A cancelled work order (cascade set status 'rejected') shows the
  // client an explicit Cancelled state, not a half-filled ladder frozen
  // at whatever rung it had reached. The derived record bars are already
  // forced terminal server-side, so the ladder would otherwise collapse
  // to "Requested" - neither honest nor clear. Short-circuit to a plain
  // Cancelled card instead.
  const woCancelled = wo?.status === "rejected";

  // Project dates use the Project entity's real column names
  // (serviceDate / scheduleDate / endDate) — NOT scheduledDate/startDate/
  // completedDate, which do not exist and would always render "-".
  const scheduledDate = project?.scheduleDate ?? project?.serviceDate ?? null;
  const completedDate = project?.endDate ?? null;
  const requestedAt = wo?.receivedAt ?? null;

  const siteContactName =
    wo?.convertedRfq?.siteContactName || quote?.siteContactName || null;
  const siteContactPhone =
    wo?.convertedRfq?.siteContactPhone || quote?.siteContactPhone || null;
  const siteContactEmail =
    wo?.convertedRfq?.siteContactEmail || quote?.siteContactEmail || null;

  const quoteAcceptable =
    !!quote && (quote.status === 0 || quote.status === 3 || quote.status === 6);

  // N2 deposit enforcement, read the way the trunk quote page reads it
  // (Quotes/components/Overview.tsx): the BILLED client carries the flag,
  // and primaryClient is the sub-client of a company client.
  const depositRequired =
    fullQuote?.client?.requiresDeposit === true ||
    fullQuote?.primaryClient?.requiresDeposit === true;
  const priceLocked = Number(quote?.status) === 6;
  const depositReceived = fullQuote?.priceLockDepositStatus === "received";
  const depositAmount = Number(fullQuote?.priceLockDepositAmount || 0);
  // The acceptance step this quote is actually on. Same state machine as
  // the trunk quote page, in client words:
  //   pay_deposit  -> price locked, deposit outstanding (Pay deposit)
  //   confirm      -> price locked, deposit in (Confirm & start)
  //   deposit      -> deposit-flagged client, not locked yet
  //                   (Pay deposit & approve -> lockPrice -> pay step)
  //   accept       -> ordinary quote, approve straight through
  //   handoff      -> the quote read failed, so we do not know which of
  //                   the above applies; send them to the quote page
  //                   rather than guess at a money path.
  const acceptStep: "pay_deposit" | "confirm" | "deposit" | "accept" | "handoff" =
    !fullQuote
      ? "handoff"
      : priceLocked
      ? depositReceived
        ? "confirm"
        : "pay_deposit"
      : depositRequired
      ? "deposit"
      : "accept";

  // Status word for the card. Price-lock vocabulary belongs to the
  // price-lock FEATURE — a client who deliberately chose to hold a price.
  // A deposit client never chose that; the lock is only the mechanism we
  // reuse underneath. Saying "Price locked" to them describes a feature
  // they did not ask for, so the deposit states get deposit words.
  // Status 6 on a client who is NOT deposit-flagged is a real price lock
  // and keeps the real label.
  const quoteStatusLabel = depositRequired
    ? depositReceived
      ? "Deposit received"
      : priceLocked
      ? "Deposit due"
      : quoteStatusWord(Number(quote?.status))
    : quoteStatusWord(Number(quote?.status));
  const invoicePayable =
    !!invoice && invoice.status !== 2 && invoice.status !== 4;

  const currentStep = ladder?.step ?? 0;
  // The live sub-step inside the CURRENT phase, resolved from the records
  // the client already has. Drives which expand-ladder dot reads current
  // and how full the current phase's bar is.
  const currentRung = (ladder?.rung ?? "Requested") as Rung;
  const activeSubIdx = currentSubStepIndex(currentRung, wo, quote, invoice, project);

  /**
   * THE ONE THING THIS CLIENT SHOULD DO NEXT (Session 80).
   *
   * The operator's case file promotes its single next step into an ink
   * card at eye level; the client page had no equivalent, so the actual
   * ask — a deposit with a due date, or an invoice to pay — sat
   * mid-page inside a record card below the ladder and the details grid.
   * A client had to read the whole page to find the one thing that was
   * waiting on them.
   *
   * It reuses the SAME handlers the quote card below uses. It does not
   * re-implement any money path: this is a second door onto one action,
   * not a second action.
   *
   * NULL when there is an outstanding cancellation request. Asking
   * someone for a deposit on work they have just asked us to stop is a
   * contradiction, and the amber banner above already owns the top of
   * the page in that state.
   */
  const clientNextStep: {
    title: string;
    body: string;
    actionLabel: string;
    onAction: () => void;
    submitting?: boolean;
  } | null = (() => {
    if (woCancelled || wo?.cancellationRequestedAt) return null;

    if (quote && quoteAcceptable && selfServiceOn && quoteLoaded) {
      const due = fullQuote?.expiresAt ? " by " + fmtDate(fullQuote.expiresAt) : "";
      if (acceptStep === "pay_deposit") {
        return {
          title:
            depositAmount > 0
              ? `Pay your deposit of ${fmtMoney(depositAmount)}`
              : "Pay your deposit",
          body: `Your price is held${due}. Once the deposit is in, we will get this on the schedule.`,
          actionLabel: "Pay deposit",
          onAction: () => {
            setLockStep("pay");
            setShowLockModal(true);
          },
        };
      }
      if (acceptStep === "deposit") {
        return {
          title: "Approve your quote",
          body: "Your account starts jobs with a deposit. Paying it approves this quote and puts the work in the queue.",
          actionLabel: "Pay deposit \u0026 approve",
          onAction: onPayDepositAndApprove,
          submitting: acting,
        };
      }
      if (acceptStep === "confirm") {
        return {
          title: "Confirm and we will start",
          body: "Deposit received. Confirming starts the job and credits the deposit to your invoice.",
          actionLabel: "Confirm \u0026 start",
          onAction: onAcceptQuote,
          submitting: acting,
        };
      }
      if (acceptStep === "accept") {
        return {
          title:
            fullQuote?.total != null
              ? `Approve your quote of ${fmtMoney(fullQuote.total)}`
              : "Approve your quote",
          body: `Approving puts this work in the queue${due ? ". Your price is held" + due : ""}.`,
          actionLabel: "Approve this quote",
          onAction: onAcceptQuote,
          submitting: acting,
        };
      }
      // "handoff" — the quote read failed, so we do not know which money
      // path applies. Say nothing here rather than guess; the quote card
      // below sends them to the full quote page.
      return null;
    }

    if (invoice && invoicePayable) {
      return {
        title:
          invoice.sequence != null
            ? `Pay invoice GMI-${pad(invoice.sequence)}`
            : "Pay your invoice",
        body: "The work is done. Paying closes this one out.",
        actionLabel: "View \u0026 pay",
        onAction: () => navigate(`/invoices/${invoice.id}`),
      };
    }

    // Nothing is waiting on the client — it is waiting on us. The ladder
    // says where it is; an empty card saying "nothing to do" is noise.
    return null;
  })();

  const milestones = CLIENT_LADDER.map((label, i) => {
    const rung = label as Rung;
    const subs =
      rung === "Quoted" && depositRequired && priceLocked
        ? depositReceived
          ? QUOTED_DEPOSIT_IN
          : QUOTED_DEPOSIT_DUE
        : PHASE_SUBSTEPS[rung] ?? [];
    const state = i < currentStep ? "done" : i === currentStep ? "current" : "future";
    // How many of THIS phase's sub-steps are reached, for the bar fill:
    // a done phase is entirely full; the current phase fills up to the
    // live sub-step (inclusive); a future phase is empty.
    const reached =
      state === "done" ? subs.length : state === "current" ? Math.min(activeSubIdx + 1, subs.length) : 0;
    return { label, rung, state, subs, reached };
  });

  return (
    <>
      <Header></Header>
      <Page title={title} $noPadding={true}>
        <Box sx={{ maxWidth: "980px", marginX: "auto", width: "100%", paddingBottom: "40px" }}>
          {/* Header */}
          <Box sx={{ marginTop: "16px", marginBottom: "18px" }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                background: "transparent",
                border: "none",
                padding: 0,
                fontFamily: "Poppins",
                fontSize: "11px",
                color: mnt.link,
                cursor: "pointer",
                marginBottom: "8px",
              }}
            >
              {"\u2039"} Back
            </button>
            <H5 fontFamily="Poppins" fontWeight={600} margin={0} padding={0} fontSize={"22px"}>
              {title}
            </H5>
            {secondary && (
              <Subtext fontSize={"13px"} fontFamily={"Poppins"} marginTop={"4px"} marginBottom={0} color={mnt.textMuted}>
                {secondary}
              </Subtext>
            )}
            <Subtext fontSize={"13px"} fontFamily={"Poppins"} marginTop={"4px"} marginBottom={0} color={mnt.textMuted}>
              {[site, cityLine].filter(Boolean).join(" \u00b7 ")}
            </Subtext>
          </Box>

          {/* CANCELLATION REQUESTED (Session 80) — the client's own
              outstanding request, at the top where they will see it
              before anything else. Amber, because this is a pending
              decision, not an error. The wording is the whole point: it
              must NEVER read as though the work has stopped. It has not
              — a provider may already be dispatched, and until a human
              confirms, the job is still live. */}
          {!woCancelled && wo?.cancellationRequestedAt && (
            <Box
              sx={{
                ...mntCard,
                padding: "16px 20px",
                marginBottom: "18px",
                background: mnt.amberPill,
                border: "1px solid " + mnt.amberStroke,
              }}
            >
              <H5 fontFamily="Poppins" fontWeight={600} margin={0} padding={0} fontSize={"14px"}>
                Cancellation requested
              </H5>
              <Subtext
                fontSize={"12.5px"}
                fontFamily={"Poppins"}
                marginTop={"6px"}
                marginBottom={0}
                color={mnt.amberDeep}
              >
                {`We have your request${
                  wo?.cancellationRequestedAt
                    ? " from " + fmtDate(wo.cancellationRequestedAt)
                    : ""
                } and someone is reviewing it. Work continues until we confirm the cancellation with you.`}
              </Subtext>
              {(wo?.cancellationReason || wo?.cancellationReasonCategory) && (
                <Subtext
                  fontSize={"12px"}
                  fontFamily={"Poppins"}
                  marginTop={"8px"}
                  marginBottom={0}
                  color={mnt.textMuted}
                >
                  {`Your reason: ${[
                    wo?.cancellationReasonCategory
                      ? CANCELLATION_REASONS.find(
                          (r) => r.value === wo.cancellationReasonCategory,
                        )?.label ?? wo.cancellationReasonCategory
                      : null,
                    (wo?.cancellationReason ?? "").trim() || null,
                  ]
                    .filter(Boolean)
                    .join(" \u2014 ")}`}
                </Subtext>
              )}
              <Flex sx={{ marginTop: "14px", gap: "10px", flexWrap: "wrap" }}>
                <BasicButton
                  type="button"
                  scheme="secondary"
                  styles={mntSecondaryBtn}
                  keepText
                  $submitting={cancelSubmitting}
                  disabled={cancelSubmitting}
                  onClick={withdrawCancellationRequest}
                >
                  Withdraw cancellation request
                </BasicButton>
              </Flex>
              {cancelError && (
                <Box
                  as="div"
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "11.5px",
                    color: "#a63d33",
                    marginTop: "10px",
                  }}
                >
                  {cancelError}
                </Box>
              )}
            </Box>
          )}

          {/* YOUR NEXT STEP (Session 80) — the ink card treatment the
              token header reserves for an action that outranks the
              page's ordinary primary, mirroring the operator case
              file's NextActionCard. It sits ABOVE the ladder because
              the ask is what the client came for; the ladder explains
              where the job is, which is the second question, not the
              first. Absent entirely when the ball is in our court. */}
          {clientNextStep && (
            <Box
              sx={{
                background: mnt.ink,
                borderRadius: "8px",
                padding: "18px 20px",
                marginBottom: "18px",
              }}
            >
              <Box
                as="div"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "10.5px",
                  fontWeight: 600,
                  letterSpacing: "0.4px",
                  textTransform: "uppercase",
                  color: mnt.onInkMuted,
                }}
              >
                Your next step
              </Box>
              <Box
                as="div"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "15px",
                  fontWeight: 600,
                  color: mnt.onInk,
                  marginTop: "8px",
                }}
              >
                {clientNextStep.title}
              </Box>
              <Box
                as="div"
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "12px",
                  color: mnt.onInkMuted,
                  marginTop: "6px",
                  lineHeight: "18px",
                }}
              >
                {clientNextStep.body}
              </Box>
              <Box sx={{ marginTop: "16px" }}>
                <BasicButton
                  type="button"
                  scheme="white"
                  styles={{ margin: "0" }}
                  $submitting={!!clientNextStep.submitting}
                  $loadingColor={mnt.ink}
                  keepText
                  onClick={clientNextStep.onAction}
                >
                  {clientNextStep.actionLabel}
                </BasicButton>
              </Box>
            </Box>
          )}

          {/* Milestone bars (Session 62 restyle) — the client timeline in
              the operator's visual language: one segmented gradient
              pill-bar per phase, filled by its own sub-steps, green done /
              blue current / gray-track future. The CURRENT phase expands
              below into its named sub-steps, so a client sees what's
              happening inside "Scheduled" or "Invoiced" the same way staff
              do — kept simple, no invented detail. Mirrors
              RecordBarStrip + RecordStepPanel (WorkOrderDetail) but on the
              client-safe 7-rung vocabulary. */}
          {woCancelled ? (
            <Box sx={{ ...mntCard, padding: "18px 20px", marginBottom: "18px" }}>
              <Box
                sx={{
                  ...mntPill("closed"),
                  fontSize: "11px",
                  display: "inline-block",
                  marginBottom: "10px",
                }}
              >
                Cancelled
              </Box>
              <H5 fontFamily="Poppins" fontWeight={600} margin={0} padding={0} fontSize={"15px"}>
                This project was cancelled
              </H5>
              <Subtext
                fontSize={"13px"}
                fontFamily={"Poppins"}
                marginTop={"6px"}
                marginBottom={0}
                color={mnt.textMuted}
              >
                {corrigoManaged
                  ? "This work order is no longer active. If you have questions, follow up in CorrigoPro."
                  : "This work order is no longer active. Reach out if you need anything further."}
              </Subtext>
            </Box>
          ) : (
          <Box sx={{ ...mntCard, padding: "18px 20px", marginBottom: "18px" }}>
            <Flex sx={{ alignItems: "flex-start", gap: "8px", flexWrap: "wrap" }}>
              {milestones.map((m) => {
                const base =
                  m.state === "done" ? mnt.green : m.state === "current" ? mnt.link : mnt.stepTrack;
                const total = Math.max(1, m.subs.length);
                const labelColor =
                  m.state === "future" ? mnt.textFaint : m.state === "current" ? mnt.link : mnt.ink;
                return (
                  <Box key={m.label} sx={{ flex: "1 1 96px", minWidth: "90px" }}>
                    {/* segmented gradient bar */}
                    <Box
                      sx={{
                        height: "16px",
                        borderRadius: "8px",
                        background: m.state === "future" ? mnt.cardBg : mnt.stepTrack,
                        border: m.state === "future" ? `1px solid ${mnt.divider}` : "none",
                        overflow: "hidden",
                        boxShadow: m.state === "current" ? `0 0 0 2px ${mnt.link}` : "none",
                      }}
                    >
                      <Flex sx={{ height: "100%", width: "100%" }}>
                        {Array.from({ length: total }).map((_, s) => (
                          <Box
                            key={s}
                            sx={{
                              flex: "1 1 0",
                              height: "100%",
                              background:
                                s < m.reached && m.state !== "future"
                                  ? mntStepShade(base, s, total)
                                  : "transparent",
                              transition: "background 200ms ease",
                            }}
                          />
                        ))}
                      </Flex>
                    </Box>
                    <Box
                      sx={{
                        fontFamily: "Poppins",
                        fontSize: "10.5px",
                        fontWeight: m.state === "current" ? 600 : 400,
                        color: labelColor,
                        marginTop: "7px",
                        textAlign: "center",
                        lineHeight: 1.25,
                      }}
                    >
                      {m.label}
                    </Box>
                  </Box>
                );
              })}
            </Flex>

            {/* Expand panel for the CURRENT phase — its named sub-steps,
                the live one highlighted. Same idea as the operator
                RecordStepPanel, in client words. */}
            {(() => {
              const active = milestones.find((m) => m.state === "current");
              if (!active || active.subs.length === 0) return null;
              const count = active.subs.length;
              return (
                <Box
                  sx={{
                    marginTop: "16px",
                    background: mnt.cardBg,
                    border: `1.5px solid ${mnt.link}`,
                    borderRadius: "8px",
                    padding: "12px 14px",
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: mnt.link,
                      marginBottom: "10px",
                    }}
                  >
                    {active.label} {"— what's happening now"}
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: ["1fr", "1fr 1fr"],
                      columnGap: "18px",
                      rowGap: "7px",
                    }}
                  >
                    {active.subs.map((label, s) => {
                      const subState =
                        s < activeSubIdx ? "done" : s === activeSubIdx ? "current" : "future";
                      const dot =
                        subState === "future"
                          ? mnt.stepTrack
                          : mntStepShade(subState === "current" ? mnt.link : mnt.green, s, count);
                      const txt =
                        subState === "done"
                          ? mnt.ink
                          : subState === "current"
                          ? mnt.link
                          : mnt.textFaint;
                      return (
                        <Flex key={label} sx={{ alignItems: "center", gap: "8px", minWidth: 0 }}>
                          <Box
                            sx={{
                              width: "9px",
                              height: "9px",
                              borderRadius: "50%",
                              background: dot,
                              flex: "0 0 auto",
                            }}
                          />
                          <Box
                            sx={{
                              fontFamily: "Poppins",
                              fontSize: "11px",
                              fontWeight: subState === "current" ? 600 : 400,
                              color: txt,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {label}
                          </Box>
                        </Flex>
                      );
                    })}
                  </Box>

                  {/* GET IT PRICED. A work order sitting at Requested with
                      no RFQ is a client watching nothing move, on a page
                      that shows them a tracker and no way to advance it.
                      They supply the quantity, the plan supplies the
                      product, and the same convert the operator's
                      "Generate RFQ" runs produces the priced RFQ.

                      Only while there is genuinely nothing priced yet:
                      no RFQ, no quote, and the WO still on its first
                      rung. */}
                  {currentRung === "Requested" &&
                    !wo?.convertedRfq?.id &&
                    wo?.servicePlan?.id &&
                    wo?.servicePlan?.clientSelfServiceEnabled && (
                      <Box
                        sx={{
                          marginTop: "14px",
                          paddingTop: "14px",
                          borderTop: "0.5px solid " + mnt.divider,
                        }}
                      >
                        <Box
                          sx={{
                            fontFamily: "Poppins",
                            fontSize: "12px",
                            color: mnt.textMuted,
                            marginBottom: "8px",
                          }}
                        >
                          Know roughly how much this one needs? Tell us and we
                          will price it now instead of you waiting on us.
                        </Box>
                        <Flex sx={{ alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                          <input
                            type="number"
                            min={1}
                            value={priceQty}
                            onChange={(e: any) => setPriceQty(e.target.value)}
                            placeholder="e.g. 110"
                            style={{
                              width: "110px",
                              padding: "8px 10px",
                              border: "0.5px solid " + mnt.divider,
                              borderRadius: "4px",
                              fontFamily: "Poppins",
                              fontSize: "12.5px",
                              color: mnt.ink,
                            }}
                          />
                          <Box
                            as="span"
                            sx={{ fontFamily: "Poppins", fontSize: "12px", color: mnt.textMuted }}
                          >
                            cubic yards
                          </Box>
                          <button
                            type="button"
                            disabled={!(Number(priceQty) >= 1) || pricing}
                            onClick={priceThisWorkOrder}
                            style={{
                              background:
                                Number(priceQty) >= 1 && !pricing ? mnt.ink : mnt.stepTrack,
                              border: "none",
                              borderRadius: "4px",
                              padding: "8px 14px",
                              fontFamily: "Poppins",
                              fontSize: "12px",
                              fontWeight: 600,
                              color: "#fff",
                              cursor:
                                Number(priceQty) >= 1 && !pricing ? "pointer" : "default",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {pricing ? "Pricing…" : "Get it priced"}
                          </button>
                        </Flex>
                        {priceError && (
                          <Box
                            sx={{
                              fontFamily: "Poppins",
                              fontSize: "11.5px",
                              color: "#a63d33",
                              marginTop: "8px",
                            }}
                          >
                            {priceError}
                          </Box>
                        )}
                      </Box>
                    )}
                </Box>
              );
            })()}
          </Box>
          )}

          {/* PRICED OPTIONS — WO_PRICING_IN_DETAIL §3 "RFQ generated"
              stage, client cut. Shown while the work order has a priced
              RFQ and no quote yet. Price and total only: no provider, no
              depot, no cost, no markup, no NTE — those ride on the shared
              RFQ payload and are operator concerns. */}
          {!woCancelled && selfServiceOn && !quote && wo?.convertedRfq?.id && (
            <Box sx={{ ...mntCard, padding: "18px 20px", marginBottom: "18px" }}>
              <Box sx={{ ...mntType.labelSm, fontWeight: 600, marginBottom: "4px" }}>
                YOUR OPTIONS
              </Box>
              {rfqLoading && !pricedOptions.length ? (
                <Box sx={{ paddingY: "14px", textAlign: "center" }}>
                  <Spinner size={22} />
                </Box>
              ) : pricedOptions.length ? (
                <>
                  <Box
                    as="div"
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "12.5px",
                      color: mnt.textMuted,
                      marginBottom: "14px",
                    }}
                  >
                    Pick how you want this one done and we will turn it into a quote for
                    you to approve.
                  </Box>
                  {pricedOptions.map((s: any) => {
                    const chosen = s?.id === selectedServiceId;
                    const unit = s?.product?.unit || "unit";
                    return (
                      <Flex
                        key={s?.id}
                        onClick={() => setSelectedServiceId(s?.id)}
                        sx={{
                          alignItems: "center",
                          gap: "12px",
                          padding: "12px 14px",
                          marginBottom: "10px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          background: chosen ? mnt.bgSubtle : mnt.cardBg,
                          border: chosen
                            ? `1.5px solid ${mnt.link}`
                            : `0.5px solid ${mnt.divider}`,
                        }}
                      >
                        <Box
                          sx={{
                            width: "16px",
                            height: "16px",
                            borderRadius: "50%",
                            flex: "0 0 auto",
                            background: mnt.cardBg,
                            border: chosen
                              ? `5px solid ${mnt.link}`
                              : `1.5px solid ${mnt.divider}`,
                          }}
                        />
                        <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
                          <Box
                            as="div"
                            sx={{
                              fontFamily: "Poppins",
                              fontSize: "13.5px",
                              fontWeight: 600,
                              color: mnt.ink,
                            }}
                          >
                            {s?.name || "Service"}
                          </Box>
                          <Box
                            as="div"
                            sx={{
                              fontFamily: "Poppins",
                              fontSize: "11.5px",
                              color: mnt.textMuted,
                              marginTop: "2px",
                            }}
                          >
                            {[
                              s?.product?.name,
                              `${Number(s?.quantity || 0)} ${unit}`.trim(),
                            ]
                              .filter(Boolean)
                              .join(" \u00b7 ")}
                          </Box>
                        </Box>
                        <Box sx={{ flex: "0 0 auto", textAlign: "right" }}>
                          <Box
                            as="div"
                            sx={{
                              fontFamily: "Poppins",
                              fontSize: "15px",
                              fontWeight: 600,
                              color: mnt.ink,
                            }}
                          >
                            {fmtMoney(serviceTotal(s))}
                          </Box>
                          <Box
                            as="div"
                            sx={{
                              fontFamily: "Poppins",
                              fontSize: "11px",
                              color: mnt.textMuted,
                              marginTop: "2px",
                            }}
                          >
                            {`${fmtMoney(s?.price)} per ${unit}`}
                          </Box>
                        </Box>
                      </Flex>
                    );
                  })}
                  <Flex sx={{ justifyContent: "flex-end", marginTop: "4px" }}>
                    <BasicButton
                      type="button"
                      scheme="primary"
                      keepText
                      $submitting={generating}
                      $loadingColor="#fff"
                      disabled={!selectedServiceId || generating}
                      onClick={onGenerateQuote}
                    >
                      Generate quote
                    </BasicButton>
                  </Flex>
                </>
              ) : (
                <Box
                  as="div"
                  sx={{ fontFamily: "Poppins", fontSize: "12.5px", color: mnt.textMuted }}
                >
                  We are putting pricing together for this one. It will show up here as
                  soon as it is ready.
                </Box>
              )}
              {panelError && (
                <Box
                  as="div"
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "11.5px",
                    color: "#a63d33",
                    marginTop: "10px",
                  }}
                >
                  {panelError}
                </Box>
              )}
            </Box>
          )}

          {/* Details */}
          <Box sx={{ ...mntCard, padding: "18px 20px", marginBottom: "18px" }}>
            <Box sx={{ ...mntType.labelSm, fontWeight: 600, marginBottom: "12px" }}>DETAILS</Box>
            <Grid columns={[1, 2]} gap={"12px 24px"}>
              <Detail label="Service" value={serviceType} />
              <Detail label="Status" value={woCancelled ? "Cancelled" : ladder?.rung} />
              <Detail label="Requested" value={fmtDate(requestedAt)} />
              <Detail label="Scheduled" value={formatDateOnly(scheduledDate)} />
              <Detail label="Completed" value={formatDateOnly(completedDate)} />
              <Detail label="Address" value={addressLine} />
            </Grid>
            {(siteContactName || siteContactPhone || siteContactEmail) && (
              <Box sx={{ marginTop: "14px", paddingTop: "14px", borderTop: "0.5px solid " + mnt.divider }}>
                <Box sx={{ ...mntType.labelSm, fontWeight: 600, marginBottom: "6px" }}>SITE CONTACT</Box>
                <Subtext fontSize={"13px"} fontFamily={"Poppins"} margin={0} color={mnt.ink}>
                  {[siteContactName, siteContactPhone, siteContactEmail].filter(Boolean).join(" \u00b7 ")}
                </Subtext>
              </Box>
            )}
          </Box>

          {/* QUOTE — WO_PRICING_IN_DETAIL §3 "quote generated" stage,
              client cut: approve or decline without leaving the work
              order. Decline goes through the SAME DeclineReasonModal +
              declineQuote path the trunk uses, so the reason category
              still reaches the decline analytics. A quote that is no
              longer actionable (accepted, declined, expired) keeps the
              plain record card. */}
          {quote && quoteAcceptable && selfServiceOn ? (
            <Box sx={{ ...mntCard, padding: "18px 20px", marginBottom: "18px" }}>
              <Flex
                sx={{
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: "14px",
                  flexWrap: "wrap",
                }}
              >
                <Box>
                  <Box
                    sx={{
                      ...mntType.labelSm,
                      fontWeight: 600,
                      color: mnt.textMuted,
                      marginBottom: "3px",
                    }}
                  >
                    QUOTE
                  </Box>
                  <Box
                    as="div"
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "15px",
                      fontWeight: 600,
                      color: mnt.ink,
                    }}
                  >
                    {quote.sequence != null ? `GMP-${pad(quote.sequence)}` : "Your quote"}
                  </Box>
                  <Box
                    as="div"
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "12px",
                      color: mnt.textMuted,
                      marginTop: "2px",
                    }}
                  >
                    {quoteStatusLabel}
                  </Box>
                </Box>
                {fullQuote?.total != null && (
                  <Box sx={{ textAlign: "right" }}>
                    <Box
                      sx={{
                        ...mntType.labelSm,
                        fontWeight: 600,
                        color: mnt.textMuted,
                        marginBottom: "3px",
                      }}
                    >
                      TOTAL
                    </Box>
                    <Box
                      as="div"
                      sx={{
                        fontFamily: "Poppins",
                        fontSize: "20px",
                        fontWeight: 600,
                        color: mnt.ink,
                      }}
                    >
                      {fmtMoney(fullQuote.total)}
                    </Box>
                  </Box>
                )}
              </Flex>

              <Flex
                sx={{
                  alignItems: "center",
                  gap: "10px",
                  flexWrap: "wrap",
                  marginTop: "16px",
                  paddingTop: "14px",
                  borderTop: "0.5px solid " + mnt.divider,
                }}
              >
                {!quoteLoaded ? (
                  <Spinner size={20} />
                ) : acceptStep === "handoff" ? (
                  <BasicButton
                    type="button"
                    scheme="primary"
                    keepText
                    onClick={() => navigate(`/quotes/${quote.id}`)}
                  >
                    {"Review \u0026 accept"}
                  </BasicButton>
                ) : (
                  <>
                    {acceptStep === "accept" && (
                      <BasicButton
                        type="button"
                        scheme="primary"
                        keepText
                        $submitting={acting}
                        $loadingColor="#fff"
                        onClick={onAcceptQuote}
                      >
                        Approve this quote
                      </BasicButton>
                    )}
                    {acceptStep === "deposit" && (
                      <BasicButton
                        type="button"
                        scheme="primary"
                        keepText
                        $submitting={acting}
                        $loadingColor="#fff"
                        onClick={onPayDepositAndApprove}
                      >
                        {"Pay deposit \u0026 approve"}
                      </BasicButton>
                    )}
                    {acceptStep === "pay_deposit" && (
                      <BasicButton
                        type="button"
                        scheme="primary"
                        keepText
                        onClick={() => {
                          setLockStep("pay");
                          setShowLockModal(true);
                        }}
                      >
                        Pay deposit
                      </BasicButton>
                    )}
                    {acceptStep === "confirm" && (
                      <BasicButton
                        type="button"
                        scheme="primary"
                        keepText
                        $submitting={acting}
                        $loadingColor="#fff"
                        onClick={onAcceptQuote}
                      >
                        {"Confirm \u0026 start"}
                      </BasicButton>
                    )}
                    {/* Decline matches the trunk's `canDecline`: active,
                        draft or expired. A price-locked quote is not
                        declined from here. */}
                    {!priceLocked && (
                      <BasicButton
                        type="button"
                        scheme="secondary"
                        outline
                        keepText
                        disabled={acting}
                        onClick={() => setShowDecline(true)}
                      >
                        Decline
                      </BasicButton>
                    )}
                  </>
                )}
                <Box
                  as="div"
                  onClick={() => navigate(`/quotes/${quote.id}`)}
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "12px",
                    color: mnt.link,
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  View the full quote
                </Box>
              </Flex>

              {quoteLoaded && acceptStep !== "accept" && (
                <Box
                  as="div"
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "11.5px",
                    color: mnt.textMuted,
                    marginTop: "10px",
                  }}
                >
                  {acceptStep === "deposit"
                    ? "Your account starts jobs with a deposit. Paying it approves this quote and puts the work in the queue."
                    : acceptStep === "pay_deposit"
                    ? `${
                        depositAmount > 0
                          ? "Deposit of " + fmtMoney(depositAmount)
                          : "Your deposit"
                      }${
                        fullQuote?.expiresAt
                          ? " is due by " + fmtDate(fullQuote.expiresAt)
                          : " is outstanding"
                      }. Once it is in, we will get this on the schedule.`
                    : acceptStep === "confirm"
                    ? "Deposit received. Confirming starts the job and credits the deposit to your invoice."
                    : "Open the quote to approve it."}
                </Box>
              )}

              {panelError && (
                <Box
                  as="div"
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: "11.5px",
                    color: "#a63d33",
                    marginTop: "10px",
                  }}
                >
                  {panelError}
                </Box>
              )}
            </Box>
          ) : quote ? (
            <RecordCard
              heading="QUOTE"
              number={quote.sequence != null ? `GMP-${pad(quote.sequence)}` : null}
              statusWord={quoteStatusWord(Number(quote.status))}
              primaryLabel={quoteAcceptable ? "Review & accept" : "View quote"}
              onPrimary={() => navigate(`/quotes/${quote.id}`)}
              emphasis={quoteAcceptable}
            />
          ) : null}

          {/* Invoice */}
          {invoice && (
            <RecordCard
              heading="INVOICE"
              number={invoice.sequence != null ? `GMI-${pad(invoice.sequence)}` : null}
              statusWord={
                invoice.statusName === "Submitted"
                  ? "Submitted"
                  : invoice.status === 2
                  ? "Paid"
                  : invoice.status === 3
                  ? "Partly paid"
                  : "Open"
              }
              primaryLabel={invoicePayable ? "View & pay" : "View invoice"}
              onPrimary={() => navigate(`/invoices/${invoice.id}`)}
              emphasis={invoicePayable}
            />
          )}

          {/* NEED TO CANCEL? (Session 80) — deliberately the last thing
              on the page and deliberately quiet: a secondary that opens
              a small form, never a button sitting beside Approve. Hidden
              once the work order is cancelled, already closed, or a
              request is outstanding (the amber banner at the top owns
              that state and carries Withdraw). */}
          {!woCancelled && !wo?.caseClosed && !wo?.cancellationRequestedAt && (
            <Box sx={{ ...mntCard, padding: "16px 20px", marginTop: "18px" }}>
              {!cancelFormOpen ? (
                <Flex
                  sx={{
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    flexWrap: "wrap",
                  }}
                >
                  <Subtext fontSize={"12.5px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
                    Need to stop this work? You can ask us to cancel it.
                  </Subtext>
                  <BasicButton
                    type="button"
                    scheme="secondary"
                    styles={mntSecondaryBtn}
                    keepText
                    onClick={() => {
                      setCancelError(null);
                      setCancelFormOpen(true);
                    }}
                  >
                    Request cancellation
                  </BasicButton>
                </Flex>
              ) : (
                <>
                  <H5 fontFamily="Poppins" fontWeight={600} margin={0} padding={0} fontSize={"14px"}>
                    Request a cancellation
                  </H5>
                  <Subtext
                    fontSize={"12.5px"}
                    fontFamily={"Poppins"}
                    marginTop={"6px"}
                    marginBottom={"12px"}
                    color={mnt.textMuted}
                  >
                    This sends the request to our team — it does not cancel the work by
                    itself. If a crew or materials are already booked, we will call you
                    before anything changes.
                  </Subtext>

                  <Box sx={{ ...mntType.labelSm, fontWeight: 600, marginBottom: "6px" }}>
                    REASON (OPTIONAL)
                  </Box>
                  <select
                    value={cancelCategory}
                    onChange={(e) => setCancelCategory(e.target.value)}
                    style={{
                      width: "100%",
                      maxWidth: "320px",
                      fontFamily: "Poppins",
                      fontSize: "13px",
                      color: mnt.ink,
                      background: mnt.cardBg,
                      border: "1px solid " + mnt.divider,
                      borderRadius: "6px",
                      padding: "8px 10px",
                      marginBottom: "10px",
                    }}
                  >
                    {CANCELLATION_REASONS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>

                  <textarea
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value.slice(0, 1000))}
                    placeholder="Anything else we should know? (optional)"
                    rows={3}
                    style={{
                      width: "100%",
                      fontFamily: "Poppins",
                      fontSize: "13px",
                      color: mnt.ink,
                      background: mnt.cardBg,
                      border: "1px solid " + mnt.divider,
                      borderRadius: "6px",
                      padding: "8px 10px",
                      resize: "vertical",
                    }}
                  />

                  <Flex sx={{ gap: "10px", marginTop: "12px", flexWrap: "wrap" }}>
                    <BasicButton
                      type="button"
                      scheme="primary"
                      keepText
                      $submitting={cancelSubmitting}
                      $loadingColor="#fff"
                      disabled={cancelSubmitting}
                      onClick={submitCancellationRequest}
                    >
                      Send request
                    </BasicButton>
                    <BasicButton
                      type="button"
                      scheme="secondary"
                      styles={mntSecondaryBtn}
                      keepText
                      disabled={cancelSubmitting}
                      onClick={() => {
                        setCancelFormOpen(false);
                        setCancelError(null);
                      }}
                    >
                      Never mind
                    </BasicButton>
                  </Flex>

                  {cancelError && (
                    <Box
                      as="div"
                      sx={{
                        fontFamily: "Poppins",
                        fontSize: "11.5px",
                        color: "#a63d33",
                        marginTop: "10px",
                      }}
                    >
                      {cancelError}
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}

          {/* Corrigo notice */}
          {corrigoManaged && (
            <Box sx={{ ...mntCard, padding: "14px 18px", background: mnt.bgBeige }}>
              <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0} color={mnt.textMuted}>
                This project is Corrigo-managed. Approvals and status updates continue in CorrigoPro;
                the summary here reflects its current state.
              </Subtext>
            </Box>
          )}
        </Box>
      </Page>

      {showDecline && (
        <DeclineReasonModal
          type="quote"
          submitting={acting}
          onConfirm={onDeclineQuote}
          onCancel={() => setShowDecline(false)}
        />
      )}

      {/* The deposit rail itself — the trunk's own modal, mounted here so
          the client pays and approves without leaving the work order. It
          owns the payment methods (saved card, Stripe, Plaid ACH) and
          writes the deposit through `recordDeposit`; nothing about that
          flow is reimplemented on this page. */}
      {showLockModal && fullQuote && (
        <PriceLockModal
          quote={fullQuote}
          settings={expirationSettings || undefined}
          initialStep={lockStep}
          depositRequired={depositRequired}
          onComplete={() => {
            setShowLockModal(false);
            setLockStep("choose");
            setReloadKey((k) => k + 1);
          }}
          onCancel={() => {
            setShowLockModal(false);
            setLockStep("choose");
          }}
        />
      )}
    </>
  );
}

function Detail({ label, value }: { label: string; value: any }) {
  return (
    <Box>
      <Box sx={{ ...mntType.labelSm, fontWeight: 600, color: mnt.textMuted, marginBottom: "2px" }}>
        {label.toUpperCase()}
      </Box>
      <Span fontFamily={"Poppins"} fontSize={"13px"} sx={{ color: value ? mnt.ink : mnt.textFaint }}>
        {value || "-"}
      </Span>
    </Box>
  );
}

function RecordCard({
  heading,
  number,
  statusWord,
  primaryLabel,
  onPrimary,
  emphasis,
}: {
  heading: string;
  number: string | null;
  statusWord: string;
  primaryLabel: string;
  onPrimary: () => void;
  emphasis?: boolean;
}) {
  return (
    <Box sx={{ ...mntCard, padding: "16px 20px", marginBottom: "18px" }}>
      <Flex sx={{ alignItems: "center", justifyContent: "space-between", gap: "14px", flexWrap: "wrap" }}>
        <Box>
          <Box sx={{ ...mntType.labelSm, fontWeight: 600, color: mnt.textMuted, marginBottom: "3px" }}>
            {heading}
          </Box>
          <Flex sx={{ alignItems: "baseline", gap: "10px", flexWrap: "wrap" }}>
            {number && (
              <Span fontFamily={"Poppins"} fontSize={"15px"} sx={{ color: mnt.ink, fontWeight: 600 }}>
                {number}
              </Span>
            )}
            <Span fontFamily={"Poppins"} fontSize={"12px"} color={mnt.textMuted}>
              {statusWord}
            </Span>
          </Flex>
        </Box>
        <BasicButton
          type="button"
          onClick={onPrimary}
          outline={!emphasis}
          scheme={emphasis ? "primary" : "secondary"}
          keepText
        >
          {primaryLabel}
        </BasicButton>
      </Flex>
    </Box>
  );
}
